import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { listMatters } from '../../actions/matters/listMatters';
import { createMatter } from '../../actions/matters/createMatter';
import { updateMatter } from '../../actions/matters/updateMatter';
import { deleteMatter } from '../../actions/matters/deleteMatter';
import type { Matter, MatterStatus } from '../../types/matter';
import PrescriptionBadge from '../../components/matters/PrescriptionBadge';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';
import {
  getAccidentDate,
  getClaimantNameOrDash,
  getIdNumber,
} from '../../utils/matter-display';

const STATUS_COLORS: Record<MatterStatus, string> = {
  draft: 'var(--t3)',
  active: 'var(--teal)',
  urgent: 'var(--warn)',
  complete: 'var(--ok)',
  archived: 'var(--t4)',
};

const STATUS_LABELS: Record<MatterStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  urgent: 'Urgent',
  complete: 'Complete',
  archived: 'Archived',
};

const FILTER_PILLS: { value: MatterStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'draft', label: 'Draft' },
  { value: 'complete', label: 'Complete' },
];

const TABLE_GRID = {
  xs: '1fr',
  md: 'minmax(0, 220px) minmax(0, 1.4fr) 140px minmax(0, 1fr) 80px 40px',
};

const matchesSearch = (m: Matter, query: string): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  const parts = [
    m.ref,
    m.userRef || '',
    getClaimantNameOrDash(m),
    getIdNumber(m),
    getAccidentDate(m),
  ];
  return parts.some((p) => p.toLowerCase().includes(q));
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const MattersListView: React.FC = () => {
  const navigate = useNavigate();
  const [matters, setMatters] = useState<Matter[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<MatterStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Matter | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMatters = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const result = await listMatters(filter);
      setMatters(result);
    } catch {
      setError('Failed to load matters.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const matter = await createMatter();
      navigate(`/matters/${matter.matterId}`);
    } catch {
      setError('Failed to create matter.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMatter(deleteTarget.matterId);
      setMatters((prev) => prev.filter((m) => m.matterId !== deleteTarget.matterId));
      setDeleteTarget(null);
    } catch {
      setError('Failed to delete matter.');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (matterId: string, nextStatus: MatterStatus) => {
    const previous = matters;
    const target = previous.find((m) => m.matterId === matterId);
    if (!target || target.status === nextStatus) return;
    setMatters((prev) =>
      prev.map((m) => (m.matterId === matterId ? { ...m, status: nextStatus } : m)),
    );
    try {
      await updateMatter(matterId, { status: nextStatus });
    } catch {
      setMatters(previous);
      setError('Failed to update status.');
    }
  };

  const stats = useMemo(() => {
    const total = matters.length;
    const inProgress = matters.filter((m) => m.status === 'active' || m.status === 'draft').length;
    const urgent = matters.filter((m) => m.status === 'urgent').length;
    const completed = matters.filter((m) => m.status === 'complete').length;
    return { total, inProgress, urgent, completed };
  }, [matters]);

  const filterCounts = useMemo(() => {
    const non = matters.filter((m) => m.status !== 'archived');
    return {
      all: non.length,
      active: non.filter((m) => m.status === 'active').length,
      urgent: non.filter((m) => m.status === 'urgent').length,
      draft: non.filter((m) => m.status === 'draft').length,
      complete: non.filter((m) => m.status === 'complete').length,
    } as Record<MatterStatus | 'all', number>;
  }, [matters]);

  const visibleMatters = useMemo(() => {
    // Hide archived by default (only show when the user explicitly filters for them).
    return matters
      .filter((m) => statusFilter === 'archived' || m.status !== 'archived')
      .filter((m) => statusFilter === 'all' || m.status === statusFilter)
      .filter((m) => matchesSearch(m, search));
  }, [matters, search, statusFilter]);

  const subtitle = useMemo(() => {
    if (matters.length === 0) return 'RAF claim matters';
    const live = matters.filter((m) => m.status !== 'archived' && m.status !== 'complete').length;
    if (live === 0) return `${matters.length} ${matters.length === 1 ? 'matter' : 'matters'} · all closed`;
    return `${live} active claim${live === 1 ? '' : 's'} · last updated today`;
  }, [matters]);

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 1.5, sm: 2, md: 4 }, pt: { xs: 2.5, sm: 3.5 }, pb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontSize: '1.75rem',
              fontWeight: 700,
              color: 'var(--t1)',
              lineHeight: 1.2,
              letterSpacing: '-0.3px',
            }}
          >
            Matters
          </Typography>
          <Typography sx={{ color: 'var(--t3)', fontSize: '0.82rem', mt: 0.75 }}>
            {subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <AddIcon sx={{ fontSize: 16 }} />}
          disabled={creating}
          onClick={handleCreate}
          sx={{
            bgcolor: 'var(--teal)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.82rem',
            textTransform: 'none',
            borderRadius: '10px',
            px: 2.5,
            py: 1.1,
            boxShadow: '0 1px 0 rgba(0,0,0,0.06), 0 4px 12px var(--teal-glow)',
            transition: 'transform 0.12s, box-shadow 0.12s',
            '&:hover': {
              bgcolor: 'var(--teal-mid)',
              transform: 'translateY(-1px)',
              boxShadow: '0 1px 0 rgba(0,0,0,0.06), 0 8px 20px rgba(26,33,102,0.28)',
            },
          }}
        >
          New Matter
        </Button>
      </Box>

      {/* Stats strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          bgcolor: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          mb: 2,
          overflow: 'hidden',
        }}
      >
        <StatCell label="Total" value={stats.total} hint="all time" />
        <StatCell label="In Progress" value={stats.inProgress} hint={stats.inProgress > 0 ? 'live work' : 'nothing in flight'} active={stats.inProgress > 0} />
        <StatCell label="Urgent" value={stats.urgent} hint={stats.urgent > 0 ? 'needs attention' : 'none flagged'} alert={stats.urgent > 0} />
        <StatCell label="Completed" value={stats.completed} hint={stats.completed > 0 ? 'closed' : '90d avg: —'} success={stats.completed > 0} />
      </Box>

      {/* Search + filter pills */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by ref, claimant, ID or accident date"
          size="small"
          sx={{
            flex: 1,
            minWidth: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'var(--bg)',
              fontSize: '0.86rem',
              '& fieldset': { borderColor: 'var(--border)' },
              '&:hover fieldset': { borderColor: 'var(--border-hi)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--teal)', borderWidth: '1px' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--t3)', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {FILTER_PILLS.map((pill) => {
            const isActive = statusFilter === pill.value;
            const count = filterCounts[pill.value] ?? 0;
            return (
              <Box
                key={pill.value}
                component="button"
                type="button"
                onClick={() => setStatusFilter(pill.value)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.75,
                  py: 0.95,
                  border: '1px solid',
                  borderColor: isActive ? 'var(--t1)' : 'var(--border)',
                  bgcolor: isActive ? 'var(--t1)' : 'var(--bg)',
                  color: isActive ? '#fff' : 'var(--t2)',
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  '&:hover': {
                    borderColor: isActive ? 'var(--t1)' : 'var(--border-hi)',
                    color: isActive ? '#fff' : 'var(--t1)',
                  },
                }}
              >
                {pill.label}
                <Box
                  component="span"
                  sx={{
                    px: 0.85,
                    py: 0.05,
                    borderRadius: '100px',
                    bgcolor: isActive ? 'rgba(255,255,255,0.15)' : 'var(--s2)',
                    color: isActive ? 'rgba(255,255,255,0.95)' : 'var(--t3)',
                    fontSize: '0.66rem',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontWeight: 500,
                  }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,94,94,0.08)', border: '1px solid rgba(255,94,94,0.2)', borderRadius: '10px' }}>
          <Typography sx={{ color: 'var(--err)', fontSize: '0.82rem' }}>{error}</Typography>
          <Button size="small" onClick={fetchMatters} sx={{ mt: 0.5, color: 'var(--teal)', textTransform: 'none', fontSize: '0.78rem' }}>
            Retry
          </Button>
        </Box>
      )}

      {loading && (
        <Box sx={{ bgcolor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={64}
              sx={{
                bgcolor: 'var(--bg2)',
                borderBottom: i < 2 ? '1px solid var(--border)' : 'none',
              }}
            />
          ))}
        </Box>
      )}

      {!loading && !error && visibleMatters.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            bgcolor: 'var(--bg)',
            borderRadius: '12px',
            border: '1px solid var(--border)',
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 48, color: 'var(--t4)', mb: 1.5 }} />
          <Typography sx={{ color: 'var(--t2)', fontSize: '0.92rem', fontWeight: 500 }}>
            {matters.length === 0 ? 'No matters yet' : 'No matters match this filter'}
          </Typography>
          <Typography sx={{ color: 'var(--t3)', fontSize: '0.82rem', mt: 0.5 }}>
            {matters.length === 0
              ? 'Create your first RAF claim matter to get started.'
              : 'Try clearing the search or changing the status filter.'}
          </Typography>
        </Box>
      )}

      <DeleteConfirmationModal
        open={deleteTarget !== null}
        fileName={
          deleteTarget
            ? `${deleteTarget.userRef ? `${deleteTarget.userRef} ` : ''}${deleteTarget.ref}`
            : ''
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        loading={deleting}
      />

      {!loading && visibleMatters.length > 0 && (
        <Box
          sx={{
            bgcolor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          {/* Column header (md+) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: TABLE_GRID.md,
              gap: 2,
              px: 2.75,
              py: 1.5,
              bgcolor: 'var(--bg2)',
              borderBottom: '1px solid var(--border)',
              fontSize: '0.66rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.7px',
              color: 'var(--t3)',
            }}
          >
            <Box>Reference</Box>
            <Box>Claimant</Box>
            <Box>Status</Box>
            <Box>Progress</Box>
            <Box sx={{ textAlign: 'right' }}>Docs</Box>
            <Box />
          </Box>

          {visibleMatters.map((m, idx) => {
            const claimantName = getClaimantNameOrDash(m);
            const accidentDate = getAccidentDate(m);
            const isLast = idx === visibleMatters.length - 1;
            return (
              <Box
                key={m.matterId}
                onClick={() => navigate(`/matters/${m.matterId}`)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: TABLE_GRID,
                  alignItems: 'center',
                  gap: 2,
                  px: 2.75,
                  py: 2,
                  borderBottom: isLast ? 'none' : '1px solid var(--border)',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 12,
                    bottom: 12,
                    width: '2px',
                    bgcolor: 'transparent',
                    borderRadius: '0 4px 4px 0',
                    transition: 'background-color 0.12s',
                  },
                  '&:hover': {
                    bgcolor: 'var(--bg2)',
                    '&::before': { bgcolor: 'var(--teal)' },
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: 'var(--t1)',
                      fontSize: '0.86rem',
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.userRef && (
                      <Box component="span" sx={{ color: 'var(--teal)', mr: 0.75 }}>
                        {m.userRef}
                      </Box>
                    )}
                    {m.ref}
                  </Typography>
                  <Typography sx={{ color: 'var(--t3)', fontSize: '0.7rem', mt: 0.4 }}>
                    Created {formatDate(m.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: 'var(--t1)', fontSize: '0.86rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {claimantName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
                    {accidentDate && (
                      <Typography sx={{ color: 'var(--t3)', fontSize: '0.7rem' }}>
                        Accident {accidentDate}
                      </Typography>
                    )}
                    {m.prescription && Object.keys(m.prescription).length > 0 && (
                      <PrescriptionBadge
                        prescription={m.prescription as {
                          date?: string;
                          daysRemaining?: number | null;
                          explanation?: string;
                          isHitAndRun?: boolean;
                          isMinor?: boolean;
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <StatusPill
                  status={m.status}
                  onChange={(next) => {
                    void handleStatusChange(m.matterId, next);
                  }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={m.progress}
                    sx={{
                      height: 4,
                      borderRadius: 100,
                      bgcolor: 'var(--s2)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, var(--teal), #00d4ff)',
                        borderRadius: 100,
                      },
                    }}
                  />
                  <Typography sx={{ color: 'var(--t3)', fontSize: '0.7rem', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    {m.progress}% complete
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: 'var(--t2)',
                    fontSize: '0.72rem',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    textAlign: 'right',
                  }}
                >
                  {(m.docsGenerated?.length ?? 0) > 0 ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 0.85,
                        py: 0.35,
                        bgcolor: 'var(--s2)',
                        color: 'var(--t2)',
                        borderRadius: '6px',
                      }}
                    >
                      {m.docsGenerated?.length} {m.docsGenerated?.length === 1 ? 'doc' : 'docs'}
                    </Box>
                  ) : (
                    ''
                  )}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  aria-label={`Delete matter ${m.ref}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(m);
                  }}
                  sx={{
                    justifySelf: 'end',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    border: 'none',
                    borderRadius: '6px',
                    bgcolor: 'transparent',
                    color: 'var(--t4)',
                    cursor: 'pointer',
                    transition: 'color 0.15s, background-color 0.15s',
                    '&:hover': {
                      color: 'var(--err)',
                      bgcolor: 'rgba(255,94,94,0.08)',
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

interface StatCellProps {
  label: string;
  value: number;
  hint: string;
  active?: boolean;
  alert?: boolean;
  success?: boolean;
}

const StatCell: React.FC<StatCellProps> = ({ label, value, hint, active, alert, success }) => {
  const color = alert
    ? 'var(--err)'
    : success
      ? 'var(--ok)'
      : active
        ? 'var(--teal)'
        : 'var(--t1)';
  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        borderRight: { md: '1px solid var(--border)' },
        borderBottom: { xs: '1px solid var(--border)', md: 'none' },
        '&:last-of-type': { borderRight: 'none', borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          color: 'var(--t3)',
          fontSize: '0.68rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          sx={{
            color,
            fontSize: '1.6rem',
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            letterSpacing: '-0.5px',
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography sx={{ color: 'var(--t3)', fontSize: '0.7rem' }}>{hint}</Typography>
      </Box>
    </Box>
  );
};

interface StatusPillProps {
  status: MatterStatus;
  onChange: (next: MatterStatus) => void;
}

const StatusPill: React.FC<StatusPillProps> = ({ status, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const color = STATUS_COLORS[status];

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = (e: React.MouseEvent | object) => {
    if ((e as React.MouseEvent).stopPropagation) {
      (e as React.MouseEvent).stopPropagation();
    }
    setAnchorEl(null);
  };
  const select = (e: React.MouseEvent<HTMLElement>, next: MatterStatus) => {
    e.stopPropagation();
    setAnchorEl(null);
    if (next !== status) onChange(next);
  };

  return (
    <>
      <Box
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status: ${STATUS_LABELS[status]}. Click to change.`}
        onClick={openMenu}
        sx={{
          justifySelf: 'start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.625,
          px: 1,
          py: 0.375,
          bgcolor: `color-mix(in srgb, ${color} 12%, transparent)`,
          border: `1px solid color-mix(in srgb, ${color} 24%, transparent)`,
          borderRadius: '6px',
          fontSize: '0.72rem',
          fontWeight: 600,
          color,
          letterSpacing: '0.01em',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background-color 0.15s, border-color 0.15s',
          '&:hover': {
            bgcolor: `color-mix(in srgb, ${color} 18%, transparent)`,
            borderColor: `color-mix(in srgb, ${color} 38%, transparent)`,
          },
          '&:focus-visible': {
            outline: 'none',
            borderColor: color,
            boxShadow: `0 0 0 2px color-mix(in srgb, ${color} 22%, transparent)`,
          },
        }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        {STATUS_LABELS[status]}
        <KeyboardArrowDownIcon sx={{ fontSize: 14, color, opacity: 0.7, ml: 0.125, mr: -0.25 }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 156,
              borderRadius: '10px',
              border: '1px solid var(--border)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              p: 0.5,
            },
          },
        }}
        MenuListProps={{ dense: true, sx: { py: 0 } }}
      >
        {(Object.keys(STATUS_LABELS) as MatterStatus[]).map((s) => {
          const isSelected = s === status;
          const sColor = STATUS_COLORS[s];
          return (
            <MenuItem
              key={s}
              onClick={(e) => select(e, s)}
              selected={isSelected}
              sx={{
                fontSize: '0.78rem',
                fontWeight: 500,
                color: 'var(--t1)',
                borderRadius: '6px',
                px: 1,
                py: 0.625,
                gap: 0.75,
                minHeight: 'auto',
                '&.Mui-selected': {
                  bgcolor: 'var(--s1)',
                  '&:hover': { bgcolor: 'var(--s2)' },
                },
                '&:hover': { bgcolor: 'var(--s1)' },
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sColor, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>{STATUS_LABELS[s]}</Box>
              {isSelected && <CheckIcon sx={{ fontSize: 14, color: 'var(--t3)' }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default MattersListView;
